// QrScan.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useQrScanner } from '../hooks/useQrScanner';
import { CameraDevice } from 'html5-qrcode';
import { Product, QrDecodedData, ScanHistoryItem, QrStockOutPayload, QrStockInPayload } from '../utils/types';
import { fetchProductByUUId, qrStockOut, qrStockIn } from '../utils/api'; // Assuming you have an API utility function to fetch product details
import { User } from '../utils/types';
import { fetchUserByUUId, validatePin, createScanHistory, fetchUsers4manual, fetchProducts4manual } from '../utils/api';
import { useSelectableData } from '../hooks/useSelectableList';
import { t } from 'i18next';
import { useStockLocationContext } from '../context/StockLocationContext';
import { useConfig } from '../context/ConfigContext';
import { hasRole } from '../utils/auth'; // Assuming you have these auth utils
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';


const PRODUCTS_PER_PAGE_MODAL = 10;

interface backendHistory {
  id?: string;
  user_id?: string;
  scanned_at?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

const fetchItems = async (type: string) => {
  if (type === "item") {

    const res = await fetchProducts4manual(1, '', null, null, 100, 'updated_at DESC');
    return res.products;

  } else {
    const res = await fetchUsers4manual(1, '', 100, 'updated_at DESC');
    return res.users;
  }
};

const QrScan: React.FC = () => {

  const { user: loggedInUser } = useAuth();
  const { settings, loadingSettings, errorSettings } = useConfig();

  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isAutocompleteVisible, setIsAutocompleteVisible] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [isMirrored, setIsMirrored] = useState(true);

  const openScanner = () => {
    setIsScannerOpen(true);
    setTimeout(() => startScanning(), 200); // small delay so modal renders first
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [stocks, setStocks] = useState<Product[]>([]);
  const [loadingStocks, setLoadingStocks] = useState(true);
  const [errorStocks, setErrorStocks] = useState<string | null>(null);

  // const [selectedLocationId, setSelectedLocationId] = useState<number | 'all'>('all');

  const [filterMode, setFilterMode] = useState<'all' | 'inStockOnly'>('all');

  // Filtered stocks
  // const filteredStocks = filterMode === 'inStockOnly'
  //     ? stocks.filter(stock => stock.total_quantity > 0)
  //     : stocks;
  const filteredStocks = stocks.filter(stock => {
    // Apply in-stock filter
    if (filterMode === 'inStockOnly' && stock.total_quantity <= 0) {
      return false;
    }

    // Apply location filter (if not "all")
    // if (selectedLocationId !== 'all') {
    //     return (
    //         stock.stock_by_location &&
    //         stock.stock_by_location[selectedLocationId] &&
    //         stock.stock_by_location[selectedLocationId] > 0
    //     );
    // }

    return true;
  });

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const loadProducts = useCallback(async () => {
    setLoadingStocks(true);
    setErrorStocks(null);

    try {
      const { products: fetchedProducts, total_pages } = await fetchProducts4manual(
        currentPage,
        searchTerm,
        null,
        null,
        PRODUCTS_PER_PAGE_MODAL,
        'updated_at DESC',
        filterMode === 'inStockOnly' ? true : undefined
      );

      // console.log('l109 qrscan fetchedProducts in qr scan >>', fetchedProducts);

      setStocks(fetchedProducts);
      setTotalPages(total_pages);
    } catch (err: any) {
      setErrorStocks(err.message || 'Failed to load stocks.');
    } finally {
      setLoadingStocks(false);
    }
  }, [currentPage, searchTerm, filterMode]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // --- Click Outside Handlers for Autocomplete ---
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsAutocompleteVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { locations, loading, error } = useStockLocationContext();
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  useEffect(() => {
    // Set default location when locations are loaded
    if (!loading && locations && locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, loading, selectedLocationId]);

  const [qrData, setQrData] = useState<any>(null);
  const [pin, setPin] = useState<string[]>(['', '', '', '']);
  const [subject, setSubject] = useState('');

  // const [pin, setPin] = useState('');
  // const [pin, setPin] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(null);

  const [lastScan, setLastScan] = useState<string | null>(null);
  const [status, setStatus] = useState("Scanner not started");
  const [pinStatus, setPinStatus] = useState("");
  const pinInputRef = useRef<HTMLInputElement>(null);

  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const [selectedUser, setSelectedUser] = useState('');

  const [userExists, setUserExists] = useState(false);
  const [scanHistoryExists, setScanHistoryExists] = useState(false);


  const {
    type, setType,
    uuid, setUuid,
    filteredItems,
    searchQuery,
    setSearch,
  } = useSelectableData({ fetchItems });

  const [scanHistory, setScanHistory] = useState<QrDecodedData[]>(() => {
    const storedScanHistory = localStorage.getItem('scanHistory');
    return storedScanHistory ? JSON.parse(storedScanHistory) : [];
  });

  const updateScanHistory = (newHistory: QrDecodedData[]) => {
    setScanHistory(newHistory);
    localStorage.setItem('scanHistory', JSON.stringify(newHistory));
  };

  const clearScanHistory = () => {
    updateScanHistory([]);
  };

  const handleSelfScan = async () => {
    if (!loggedInUser) {
      toast.error("You must be logged in to perform this action.");
      return;
    }

    if (scanHistory.some(item => item.type === 'user')) {
      toast.warn("Only one user can be scanned at a time.");
      return;
    }

    // Reset PIN input
    setPin(['', '', '', '']);
    setPinStatus('');

    const qrDataPayload: QrDecodedData = {
      type: 'user',
      id: loggedInUser.user_uuid,
      ts: new Date().toISOString(),
      action: '',
    };

    // console.log('qrDataPayload >>', qrDataPayload);

    setQrData(qrDataPayload);
    setUser(loggedInUser);
    setProduct(null);
    setUserExists(false); // This will show the PIN input

    if (!scanHistoryExists) {
      const scanHistoryPayload = { ...qrDataPayload, user_id: loggedInUser.id, type: qrDataPayload.type, ts: qrDataPayload.ts };
      const newHistory = await createScanHistory(scanHistoryPayload);
      setScanHistoryId(newHistory?.id || null);
      setScanHistoryExists(true);
    }

    setTimeout(() => pinInputRef.current?.focus(), 100);
  };

  const handleProductSelect = (product: Product) => {

    let quantityDisplayClass = 'text-black';
    const defaultQty = 1;

    if (selectedLocationId !== null) {
      const stockAtLocation = product.stock_by_location?.[selectedLocationId];
      if (typeof stockAtLocation !== 'number' || defaultQty > stockAtLocation) {
        toast.error(`Not enough stock for "${product.product_name}" at ${getLocationName(selectedLocationId)}.`);
        quantityDisplayClass = 'text-red-700';
      }
    } else {
      toast.warn("No location selected. Stock availability not checked.");
    }

    const newItem: QrDecodedData = {
      type: 'item',
      id: product.product_uuid,
      ts: new Date().toISOString(),
      product: product,
      scan_history_id: scanHistoryId || 0,
      // quantity: 1,
      quantity: defaultQty,
      action: '',
      // quantityDisplayClass: 'text-black', // Initialize default class
      quantityDisplayClass,
    };

    console.log("handleProductSelect newItem >>", newItem);

    setScanHistory(prev => [...prev, newItem]);
    setSearchTerm(''); // Clear search term
    setIsAutocompleteVisible(false); // Hide autocomplete
    toast.success(`${product.product_name} added to list.`);
  };

  // When you want to add a new scan to the history
  const addScanToHistory = (newScan: QrDecodedData) => {
    if (newScan.type === 'user') {
      if (scanHistory.some(item => item.type === 'user')) {
        toast.warn("Only one user can be scanned at a time.");
        return;
      }
    }
    updateScanHistory([...scanHistory, newScan]);
  };

  const [scanHistoryId, setScanHistoryId] = useState<number | null>(null);

  let backendHistory: any;

  const {
    startScanning,
    stopScanning,
    switchCamera,
    getCameras,
    applyZoom,          // ADD
    getZoomCapabilities, // ADD
    toggleTorch,        // ADD

  } = useQrScanner({

    elementId: "reader",

    onSuccess: async (text) => { // Make this function async

      // console.log("QR Code scanned:", text);
      // alert(`QR Code scanned: ${text}`);

      setLastScan(text);
      setStatus("✅ Scan successful!");
      toast.success("✅ Scan successful!");


      // stop scanner and close modal
      stopScanning();
      setIsScannerOpen(false);
      setIsTorchOn(false);   // ADD — avoid stale UI state


      let decodedData: QrDecodedData;
      const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

      try {
        // First, try to parse it as a JSON object
        decodedData = JSON.parse(text) as QrDecodedData;

        // console.log("l316 Decoded QR data:", decodedData);
        // alert(`Decoded QR data: ${JSON.stringify(decodedData)}`);

      } catch (error) {

        // If parsing fails, check if it's a plain UUID
        if (uuidRegex.test(text)) {

          // console.log("Scanned text is a plain UUID. Creating a QrDecodedData object.", text);

          decodedData = {
            id: text,
            type: 'item', // Assume 'item' for old UUID-only QR codes
            action: '',
            ts: new Date().toISOString(),
            quantity: 1,
            quantityDisplayClass: 'text-black',
            scan_history_id: 0,
            product: null,
          };



          // console.log("Created decodedData from UUID:", decodedData);
          // alert(`Created decodedData from UUID: ${JSON.stringify(decodedData)}`);

        } else {
          toast.error("Invalid QR Code format.");
          // console.error("Scanned text is not valid JSON or a UUID:", text);
          // alert("Invalid QR Code format! Nothing Found?"); // Add this line for the alert
          return; // Stop processing if the format is unknown
        }

      }

      if (
        decodedData.type === 'user' &&
        // scanHistory.some((s) => s.type === 'user'
        scanHistory.find(h => h.type === 'user' && h.user)
      ) {
        setUserExists(true);
        setStatus("⚠️ User already scanned.");
        toast.warn("⚠️ User already scanned.");
        return;
      }


      let scanHistoryPayload: any = {
        type: decodedData.type,
        id: decodedData.id,
        ts: decodedData.ts || new Date().toISOString(),
        // Add more fields as needed (e.g., user_id, item_id, etc.)
      };

      // Optionally add more info for user or item
      if (decodedData.type === 'user' && !userExists) {
        setPin(['', '', '', '']);  // Reset PIN when scanning a new user
        setPinStatus('');

        const user = await fetchUserByUUId(decodedData.id);
        // console.log("ln 137 Fetched user:", user);

        setQrData(decodedData);  // store raw QR data
        setUser(user);           // show user details
        setProduct(null);
        setUserExists(false);    // force PIN entry

        scanHistoryPayload.user_id = user.id;

        // Don't push into scanHistory here!
        // Just store backend scanHistoryId if needed
        if (!scanHistoryExists) {
          const newHistory = await createScanHistory(scanHistoryPayload);
          setScanHistoryId(newHistory?.id || null);
          setScanHistoryExists(true);
        }

        // ⬇️ Auto-focus first PIN input
        setTimeout(() => {
          pinInputRef.current?.focus();
        }, 100);

      }


      if (decodedData.type === 'item') {

        const product = await fetchProductByUUId(decodedData.id);

        // console.log("l397 Fetched product:", decodedData.id, product);

        let quantityDisplayClass = 'text-black';
        const defaultQty = 1;

        // console.log("product.stock_by_location:", product.stock_by_location);
        // console.log("selectedLocationId:", selectedLocationId);

        if (selectedLocationId !== null) {

          const stockAtLocation = product.stock_by_location?.[selectedLocationId];

          if (typeof stockAtLocation !== 'number' || defaultQty > stockAtLocation) {
            toast.error(`Not enough stock for "${product.product_name}" at ${getLocationName(selectedLocationId)}.`);
            quantityDisplayClass = 'text-red-700';
          }
        } else {
          toast.warn("No location selected. Stock availability not checked.");
        }

        const newItem = {
          ...decodedData,
          product,
          scan_history_id: scanHistoryId || 0,
          quantity: defaultQty,
          quantityDisplayClass,
        };

        setQrData(decodedData);
        setUser(null);
        setProduct(product);
        // setScanHistory((prev) => [...prev, { ...decodedData, product, scan_history_id: scanHistoryId || 0 }]);

        setScanHistory(prev => [...prev, newItem]);

      }
    },

    onIdleTimeout: () => {
      setStatus("⏳ Scanner timed out");
      toast.error("⏳ Scanner timed out")
    },
  });

  const [isTorchOn, setIsTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(true);

  const handleToggleTorch = async () => {
    const nextState = !isTorchOn;
    const success = await toggleTorch(nextState);

    if (!success) {
      setTorchSupported(false);
      toast.warn("Flashlight not supported on this device.");
      return;
    }

    setIsTorchOn(nextState);
  };

  useEffect(() => {
    getCameras().then(setCameras);
  }, []);

  const [isZoomed, setIsZoomed] = useState(false);
  const [hardwareZoomSupported, setHardwareZoomSupported] = useState(true);

  const toggleZoom = async () => {
    const targetZoom = isZoomed ? 1 : 2;
    const applied = await applyZoom(targetZoom);

    if (!applied) {
      // Hardware zoom not available on this device/browser — fall back to CSS scale
      setHardwareZoomSupported(false);
    }
    setIsZoomed(!isZoomed);
  };

  const handleSubmitPin = async (enteredPin: string) => {
    if (enteredPin.length !== 4) return;

    if (!qrData) {
      toast.error("Cannot submit, no user QR data available.");
      return;
    }

    try {
      const data = await validatePin(qrData.id, enteredPin);
      if (data.success) {
        const user = await fetchUserByUUId(qrData.id);
        const newScanForUser = { ...qrData, user, scan_history_id: scanHistoryId || 0 };

        setScanHistory(prevHistory => {
          // Filter out any previous user that might be in the list
          const historyWithoutUser = prevHistory.filter(item => item.type !== 'user');

          // Update all existing items with the correct scan_history_id
          const updatedItems = historyWithoutUser.map(item => ({
            ...item,
            scan_history_id: scanHistoryId || 0,
            quantityDisplayClass: item.quantityDisplayClass || 'text-black', // Ensure class is preserved or defaulted
          }));

          // Add the newly confirmed user to the history
          return [...updatedItems, newScanForUser];
        });

        setUserExists(true);
        setPin(['', '', '', '']);
        setPinStatus("✅ PIN valid.");
      } else {
        setPinStatus("❌ Invalid PIN.");
        setPin(['', '', '', '']);
      }
    } catch (error: any) {
      setPinStatus("❌ Invalid PIN.");
      setPin(['', '', '', '']);
      toast.error(error.message || 'An unknown error occurred.');
    }
  };



  // Load history from localStorage on mount
  useEffect(() => {
    const storedScanHistory = localStorage.getItem('scanHistory');
    if (storedScanHistory) {
      setScanHistory(JSON.parse(storedScanHistory));
    }
  }, []);

  // Sync scanHistory to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('scanHistory', JSON.stringify(scanHistory));
  }, [scanHistory]);


  const handleRemoveScanHistoryItem = (index: number) => {
    setScanHistory((prevHistory) => {
      const newHistory = prevHistory.filter((_, i) => i !== index);
      if (prevHistory[index].type === 'user') {
        setUserExists(false);
      }
      return newHistory;
    });
  };

  const handleSubmitStockOut = async () => {
    const userEntry = scanHistory.find(h => h.type === 'user' && h.user);
    const itemEntries = scanHistory.filter(h => h.type === 'item' && h.product);

    if (!userEntry || !userEntry.user) {
      setStatus("❌ No valid user QR scanned.");
      toast.error("❌ No valid user QR scanned.");
      return;
    }

    if (itemEntries.length === 0) {
      setStatus("❌ No product items scanned.");
      toast.error("❌ No product items scanned.");
      return;
    }

    if (!selectedLocationId) {
      toast.error("Please select a stock location first.");
      return;
    }

    const items = itemEntries.map(entry => ({
      product_id: entry.product.id,
      item_id: entry.id,
      quantity: entry.quantity || 1,
      scan_history_id: entry.scan_history_id,
    }));

    const qrStockOutPayload: QrStockOutPayload = {
      scan_history_id: scanHistoryId || 0,
      subject: subject || 'Stock out via QR scan',
      user_id: userEntry.id, // UUID, not DB ID
      location_id: selectedLocationId,
      reason: 'Stock out via QR scan',
      items,
    };

    try {
      const data = await qrStockOut(qrStockOutPayload);

      // console.log("Stock out data:", data);
      // console.log("qrStockOutPayload:", qrStockOutPayload);
      // console.log("scanHistoryId:", scanHistoryId);

      if (data.success === true) {
        setStatus("✅ Stock out successful!");
        toast.success("✅ Stock out successful!");

        // Remove items and user from scan history
        setScanHistory([]);
        setScanHistoryId(null);
        setUserExists(false);
        setUser(null);
        setPin(['', '', '', '']);  // Reset PIN when scanning a new user
        setSubject('');
      } else {
        setStatus(data.message || "❌ Stock out failed.");
        toast.error(data.message || "❌ Stock out failed.");
      }
    } catch (error: any) {
      setStatus(error.message || '❌ An unknown error occurred.');
      toast.error(error.message || '❌ An unknown error occurred.');
      console.error("Stock out error:", error);
    }
  };

  const handleSubmitStockIn = async () => {
    const userEntry = scanHistory.find(h => h.type === 'user' && h.user);
    const itemEntries = scanHistory.filter(h => h.type === 'item' && h.product);

    if (!userEntry || !userEntry.user) {
      setStatus("❌ No valid user QR scanned.");
      toast.error("❌ No valid user QR scanned.");
      return;
    }

    if (itemEntries.length === 0) {
      setStatus("❌ No product items scanned.");
      toast.error("❌ No product items scanned.");
      return;
    }

    if (!selectedLocationId) {
      toast.error("Please select a stock location first.");
      return;
    }

    const items = itemEntries.map(entry => ({
      product_id: entry.product.id,
      item_id: entry.id,
      quantity: entry.quantity || 1,
      scan_history_id: entry.scan_history_id,
    }));

    const qrStockInPayload: QrStockInPayload = {
      scan_history_id: scanHistoryId || 0,
      subject: subject || 'Stock in via QR scan',
      user_id: userEntry.id, // UUID, not DB ID
      location_id: selectedLocationId,
      reason: 'Stock in via QR scan',
      items,
    };

    try {
      const data = await qrStockIn(qrStockInPayload);

      // console.log("Stock in data:", data);
      // console.log("qrStockInPayload:", qrStockInPayload);
      // console.log("scanHistoryId:", scanHistoryId);

      if (data.success === true) {
        setStatus("✅ Stock in successful!");
        toast.success("✅ Stock in successful!");

        // Remove items and user from scan history
        setScanHistory([]);
        setScanHistoryId(null);
        setUserExists(false);
        setUser(null);
        setPin(['', '', '', '']);  // Reset PIN when scanning a new user
        setSubject('');
      } else {
        setStatus(data.message || "❌ Stock in failed.");
        toast.error(data.message || "❌ Stock in failed.");
      }
    } catch (error: any) {
      setStatus(error.message || '❌ An unknown error occurred.');
      toast.error(error.message || '❌ An unknown error occurred.');
      console.error("Stock in error:", error);
    }
  };


  useEffect(() => {
    const userEntry = scanHistory.find((entry) => entry.type === 'user' && entry.user);
    const itemEntry = scanHistory.find((entry) => entry.type === 'item' && entry.product);

    // console.log("User Entry:", userEntry);
    // console.log("Item Entry:", itemEntry);

    if (userEntry && userEntry.user.length > 0) {
      setUser(userEntry.user);
      // setUserExists(true);
      setScanHistoryId(itemEntry?.scan_history_id || null);
    } else {
      setUser(null);
      // setUserExists(false);
    }
  }, [scanHistory]);

  const isReadyToStockIn = !!(
    scanHistory.find(h => h.type === 'user' && h.user) &&
    scanHistory.find(h => h.type === 'item' && h.product)
  );

  // let quantityStyle = ' text-black';

  const updateItemQuantity = (index: number, newQty: number, item: QrDecodedData, selectedLocationId: number) => {

    let newQuantityDisplayClass = 'text-black'; // Default to black

    // Check if the stock_by_location object exists and if the locationId is a valid key
    // console.log("item:", item);
    // console.log("selectedLocationId:", selectedLocationId);

    // Check if selectedLocationId exists as a key in item.product.stock_by_location
    if (!item.product.stock_by_location || !(selectedLocationId in item.product.stock_by_location)) {

      toast.error('Error: is this item "' + item.product.product_name + '" in Stock at ' + getLocationName(Number(selectedLocationId)) + '?');

      newQuantityDisplayClass = 'text-red-700';
    }

    if (item.product.stock_by_location && item.product.stock_by_location[selectedLocationId]) {
      const stockByLocation = item.product.stock_by_location[selectedLocationId];
      // Compare the newQuantity with the stock_by_location quantity of the locationId

      if (newQty > stockByLocation) {

        // Show an error message
        toast.error('Error: Quantity cannot be greater than stock_by_location quantity of the locationId');
        // return;

        // Set the color of the quantity to red
        // quantityStyle = ' text-red-700';
        newQuantityDisplayClass = 'text-red-700';

      } else {
        // Set the color of the quantity to green
        // quantityStyle = ' text-black';  
        newQuantityDisplayClass = 'text-black';


      }
    }

    setScanHistory(prev => prev.map((entry, i) =>
      // i === index ? { ...entry, quantity: newQty } : entry

      i === index ? { ...entry, quantity: newQty, quantityDisplayClass: newQuantityDisplayClass } : entry

    ));
  };

  const isReadyToStockOut = (() => {
    const hasUser = !!scanHistory.find(h => h.type === 'user' && h.user);
    const itemEntries = scanHistory.filter(h => h.type === 'item' && h.product);
    const hasItems = itemEntries.length > 0;

    if (!hasUser || !hasItems || selectedLocationId === null) {
      return false;
    }

    // Check if every item in the list has sufficient stock for the desired quantity
    const allItemsHaveEnoughStock = itemEntries.every(item => {
      // Ensure stock_by_location and the specific location's stock exist and are numbers
      const availableStock = item.product.stock_by_location?.[selectedLocationId];
      if (typeof availableStock !== 'number') {
        // If stock info is missing or not a number, it's not available.
        return false;
      }
      // Compare the desired quantity with the available stock
      return item.quantity <= availableStock;
    });

    return allItemsHaveEnoughStock;
  })();

  const getLocationName = (locationId: number | string) => {
    const id = Number(locationId);
    const location = locations.find((loc) => loc.id === id);
    return location ? location.name : 'N/A';
  };

  // console.log('scanHistory:', scanHistory);


  return (
    <div className="p-1">

      <div className='flex flex-row p-0 m-0'>

        {/* Scanner Controls and Status */}
        <div className='text-xs md:text-sm w-1/2 md:w-full flex flex-col  items-center mt-0 border-t-2 p-1 md:p-4'>

          <div className='flex flex-row justify-center items-center w-full'>

            <button
              onClick={openScanner}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Scan QR (F.S.)
            </button>

            {/* <button onClick={() => startScanning()} className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-150 disabled:opacity-50'>Start</button> */}
            <button onClick={stopScanning} className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition duration-150 disabled:opacity-50'>Stop</button>

          </div>

          <div className='p-2 text-xs'>
            <label>⚙️ Switch Camera:</label>
            <select onChange={(e) => switchCamera(e.target.value)} className="max-w-xs text-xs">
              {cameras.map((cam) => {
                const label = cam.label || "Camera";
                const shortLabel = label.split(' (')[0];
                return (
                  <option key={cam.id} value={cam.id}>{shortLabel}</option>
                );
              })}
            </select>

          </div>

          <p>Status: {status}</p>

          {status === "Scanner not started" && (
            <h1 className='text-red-500'>Please start the scanner</h1>
          )}
          {status === "⏳ Scanner timed out" && (
            <h1 className='text-red-500'>Scanner timed out</h1>
          )}
          {/* {!stopScanning && ( */}
          {/* <div id="reader" style={{ width: '100%', aspectRatio: '4/3', maxWidth: '480px', marginBottom: 12 }} className="md:scale-x-[-1]" /> */}
          {/* )} */}

          {/* <div id="reader" style={{ width: 220, height: 220, marginBottom: 12 }} className="scale-x-[-1]" /> */}

        </div>

        {/* //location select */}
        <div className='w-1/2 md:w-full flex flex-col md:flex-row justify-between items-center mb-1'>

          {/* //location select */}
          <div className='text-xs md:text-sm px-2 w-full md:w-1/2'>
            <label htmlFor="location-select" className="mr-2 font-semibold">Branch:</label>
            <select
              id="location-select"
              value={selectedLocationId || ''}
              onChange={(e) => setSelectedLocationId(Number(e.target.value))}
              className="max-w-xs  text-xs md:text-sm border p-1 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            >
              {loading && <option>Loading...</option>}
              {error && <option>Error loading locations</option>}
              {!loading && !error && locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>

          {/* //subject */}
          <div className='text-xs md:text-sm px-2 w-full md:w-1/2'>
            <label htmlFor="subject" className="mr-2 font-semibold">Subject:</label>
            <input type="text" name='subject' id='subject' placeholder='Subject' className='w-full  text-xs md:text-sm   border p-1 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
        </div>

      </div>

      {/* // super admin abb 260712 */}
      {/* {hasRole(['super_admin']) && ( */}

      <div className="my-2 flex flex-col md:flex-row md:items-center md:justify-between bg-green-200 rounded-xl p-2">
        {/* <h3 className='text-orange-700 font-bold'>Super Admin : </h3> */}
        <div className="relative w-full md:w-1/2 mb-2" ref={searchContainerRef}>
          <div className="flex items-center">
            <input
              type="text"
              placeholder={t('productSelector.searchPlaceholder') as string}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
                setIsAutocompleteVisible(true);
              }}
              onFocus={() => setIsAutocompleteVisible(true)}
              className="w-full p-2 border border-gray-300 rounded"
              onKeyDown={e => {
                if (searchTerm && e.key === 'Enter') {
                  e.preventDefault();
                  // handleCreateNewItem();
                }
              }}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => { setSearchTerm(''); setIsAutocompleteVisible(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
              >
                &times;
              </button>
            )}
          </div>

          {isAutocompleteVisible && searchTerm && stocks.length > 0 && (
            <ul className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-60 overflow-y-auto">
              {stocks.map(product => (
                <li
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  className="p-2 hover:bg-blue-100 cursor-pointer text-sm"
                >
                  {product.product_name} <span className="text-xs text-gray-500">({product.product_code})</span>
                </li>
              ))}
            </ul>
          )}
          {searchTerm && !loadingStocks && stocks.length === 0 && isAutocompleteVisible && (
            <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-20 p-2 text-sm text-gray-500">
              No products found.
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleSelfScan}
          className="w-auto md:w-auto mx-4 px-4 py-1 bg-orange-500 text-sm text-white rounded hover:bg-orange-600"
          title="Emulate scanning your own QR code to perform stock actions for yourself."
        >
          {/* Scan Self to Actions */}
          Manually Add Product (Emulate QR Scan)
        </button>
      </div>

        {/* // super admin abb 260712 */}
      {/*  )} */}

      {/* // Main Scan List Display Area */}
      {/* {!qrData && ( */}
      <>

        {/* <div className="p-1 flex flex-col md:flex-row  items-start w-full text-xs md:text-sm"> */}
        <div className="p-1 flex flex-col md:flex-col  items-start w-full text-xs md:text-sm">

          {/* // abb 251214v SHOULD PLAN TO MAKE THIS AS Modal!!! last scan, product details, and user data */}
          {/* {!hasRole(['super_admin']) && ( */}
          <>
            {/* Display last scan, product details, and user data */}
            {/* <div className='w-full md:w-1/2 flex flex-col  items-center mb-4 border-x-2  p-1 md:p-4'> */}
            <div className='w-full flex flex-col  items-center mb-4 border-x-2  p-1 md:p-4'>

              {lastScan && (
                <p className='text-lg text-green-600'>Last Scan: {new Date().toLocaleString()}</p>
              )}

              {product && (
                <div>
                  <h2>Product Details</h2>
                  <p><strong>Product Name:</strong> {product.product_name}</p>
                  <p><strong>Product Code:</strong> {product.product_code}</p>
                  <p><strong>Product Value:</strong> {product.product_value}</p>

                  {/* <img src={product.media[0].preview_url || product.media[0].original_url || window.APP_CONFIG?.PUBLIC_URL + '/placeholder.jpg'} alt={product.product_name} /> */}
                </div>
              )}

              {/* // Display user data if available */}
              {user && (
                <div>
                  <p><strong>Action:</strong> {qrData?.action}</p>
                  {/* <p><strong>ID:</strong> {qrData.id}</p> */}
                  <p><strong>Name:</strong> {user.name}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>Phone:</strong> {user.phone}</p>

                  {userExists === true && (
                    <>
                      <p className="text-gray-500 text-sm mt-2 bg-pink-100 p-2 rounded-xl mb-2">User exist! Remove it first</p>
                    </>
                  )}

                  {userExists === false && (
                    <>

                      <p className="text-gray-500 text-sm mt-2">Enter PIN</p>


                      <div className="flex space-x-2 mt-1 mb-2">
                        {pin.map((digit, index) => (
                          <input
                            key={index}
                            ref={index === 0 ? pinInputRef : null}  // attach only to the first one

                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              if (!val) return;

                              const newPin = [...pin];
                              newPin[index] = val;
                              setPin(newPin);

                              // Move focus automatically
                              if (index < 3) {
                                (e.target.nextElementSibling as HTMLInputElement)?.focus();
                              }

                              // Submit only AFTER all 4 digits are filled
                              if (newPin.every(d => d !== '')) {
                                setTimeout(() => handleSubmitPin(newPin.join('')), 150);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Backspace') {
                                const newPin = [...pin];
                                if (pin[index]) {
                                  newPin[index] = '';
                                  setPin(newPin);
                                } else if (index > 0) {
                                  newPin[index - 1] = '';
                                  setPin(newPin);
                                  (e.currentTarget.previousElementSibling as HTMLInputElement)?.focus();
                                }
                              }
                            }}
                            className="w-12 h-12 text-center text-2xl border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pin-mask"
                          />
                        ))}
                      </div>
                      <p className="text-gray-500 text-sm">{pinStatus}</p>

                    </>
                  )}

                </div>
              )}

            </div>
          </>
          {/* )} */}



          {/* Display scan history and user details */}
          {/* This section will show the last scan, product details, and user data */}
          {/* If qrData is available, it will show the action and ID */}
          {/* If product is available, it will show product details */}
          {/* If user is available, it will show user details */}
          <div className={`w-full ${!hasRole(['super_admin']) ? 'md:w-1/2' : 'md:w-full'
            } flex flex-col items-center border-l-2 p-1 md:p-4`}
          >

            <h2 className='text-lg text-orange-600'>{getLocationName(Number(selectedLocationId))} - Scan History
              {scanHistoryId && <span> - ID: {scanHistoryId} </span>}
            </h2>

            <div>

              <button
                className='bg-green-600 hover:bg-green-800 hover:shadow-lg  text-white font-bold py-1 px-2 rounded transition duration-150 mx-2cmb-2 disabled:opacity-50'
                onClick={handleSubmitStockIn}
                // disabled={!scanHistory.some(item => item.type === 'user')}
                disabled={!isReadyToStockIn}

              >
                Stock In
              </button>

              <button
                className='bg-blue-600 hover:bg-blue-800 hover:shadow-lg text-white font-bold py-1 px-2 rounded transition duration-150 mx-2 mb-2 disabled:opacity-50'
                onClick={handleSubmitStockOut}
                // disabled={!scanHistory.some(item => item.type === 'user')}
                disabled={!isReadyToStockOut}

              >
                Stock Out
              </button>

              <button
                className='bg-gray-400 hover:bg-gray-600 text-white font-bold py-1 px-2 rounded transition duration-150 mb-2'
                onClick={clearScanHistory}
              >
                Clear All History
              </button>
            </div>

            {/* // scanned items list */}
            <div className='w-full bg-white shadow-md rounded-lg mb-2 px-4 pb-12 text-xs text-gray-500 '>
              <ol className='list-decimal pl-4 scrollable h-screen'>
                {scanHistory.map((item, index) => (


                  <li key={index} className='flex flex-col items-end md:flex-row md:justify-between my-2 border-b-1 border-gray-400 last:border-b-0'>

                    {item.type === 'user' && item.user && (
                      <div className='bg-green-100 p-2 rounded-xl mb-2'>
                        <p><strong>User Name:</strong> {item.user.name}</p>
                        <p><strong>User Email:</strong> {item.user.email}</p>
                      </div>
                    )}

                    {/* {console.log("line 1104 scan history item:", item)} */}

                    {item.type === 'item' && item.product && (
                      <div className='p-2 flex items-center space-x-2'>
                        <img
                          className="h-12 w-12 rounded-md object-cover"
                          src={item.product.media && item.product.media.length > 0 ? item.product.media[0].preview_url : window.APP_CONFIG?.PUBLIC_URL + '/placeholder.jpg'}
                          alt={item.product.product_name || `Product ${item.product.id}`}
                        />
                        <div>
                          <p>
                            <strong>Product Name:</strong> [{item.product.id}] {item.product.product_name}
                          </p>

                          <p><strong>Product Code:</strong> {item.product.product_code}</p>

                          {/* stock location info abb 260712 */}
                          <p><strong>Stock @ location:</strong>
                            {/* {stock.stock_by_location ? ( */}
                            {item.product.stock_by_location ? (
                              Object.entries(item.product.stock_by_location)
                              // Object.entries(stock.stock_by_location)
                                // .filter(([locationId]) => Number(locationId) > 0)
                                .map(([locationId, quantity]) => (
                                  <p key={locationId}>
                                    {getLocationName(Number(locationId))}: {quantity as number}
                                    {Number(locationId) === selectedLocationId && <span className='text-blue-500'> (Selected Branch)</span>}
                                    <br />
                                  </p>
                                ))
                            ) : 'N/A'}
                          </p>

                        </div>
                      </div>
                    )}

                    <div className='p-2 flex flex-col items-center space-x-2'>
                      {/* Quantity controls */}
                      {item.type === 'item' && item.product && (
                        <div className="flex items-center space-x-1 mt-1">
                          <button
                            onClick={() => {
                              if (selectedLocationId !== null) {
                                updateItemQuantity(index, item.quantity - 1, item, selectedLocationId)
                              }
                            }
                            }
                            disabled={item.quantity <= 1}
                            className="px-2 bg-gray-300 rounded text-xl"
                          >
                            &nbsp;-&nbsp;
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            min={1}
                            onChange={(e) => {
                              if (selectedLocationId !== null) {
                                updateItemQuantity(index, parseInt(e.target.value) || 1, item, selectedLocationId)
                              }
                            }}
                            // className={`bg-green-100 w-12 text-xl font-bold text-center border rounded ${quantityStyle}`}

                            className={`bg-green-100 w-12 text-xl font-bold text-center border rounded ${item.quantityDisplayClass || 'text-black'}`}
                          />
                          <button
                            onClick={() => {
                              if (selectedLocationId !== null) {
                                updateItemQuantity(index, item.quantity + 1, item, selectedLocationId)
                              }
                            }}
                            className="px-2 bg-gray-300 rounded text-xl"
                          >
                            &nbsp;+&nbsp;
                          </button>
                        </div>
                      )}

                      {/* Add other types of items here */}
                      <button
                        className='mb-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded transition duration-150'
                        onClick={() => handleRemoveScanHistoryItem(index)}
                      >
                        {item.type === 'user' ? 'Remove User' : 'Remove Item'}
                      </button>

                    </div>

                  </li>
                ))}
              </ol>

            </div>
          </div>

        </div>

      </>


      {isScannerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center">
          <span className='absolute top-4 left-4 bg-green-500 text-white px-4 py-2 rounded z-10'>
            {settings?.shortname && settings.shortname.length > 10
              ? settings.shortname.split(/\s+/).map((word: string) => word[0]).join('').toUpperCase()
              : (settings?.shortname || t('header.companyName'))}&nbsp;
            [ {getLocationName(Number(selectedLocationId))} ]
          </span>
          <button
            onClick={() => {
              stopScanning();
              setIsScannerOpen(false);
              setIsTorchOn(false);   // ADD — avoid stale UI state

            }}
            className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded z-10"
          >
            ✕ Close
          </button>
          <button
            onClick={() => setIsMirrored(!isMirrored)}
            className="absolute top-16 right-4 bg-blue-500 text-white px-4 py-2 rounded z-10"
          >
            {isMirrored ? 'Un-mirror' : 'Mirror'} Camera
          </button>

          {/* <div
            id="reader"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '900px', // big but still responsive
              aspectRatio: '4/3',
              position: 'relative', // Added for positioning the scan line
              overflow: 'hidden',   // Added to contain the scan line
            }}
            className={isMirrored ? 'scale-x-[-1]' : ''}
          /> */}

          <div
            id="reader"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '900px',
              aspectRatio: '4/3',
              position: 'relative',
              overflow: 'hidden',
              transform: [
                isMirrored ? 'scaleX(-1)' : '',
                !hardwareZoomSupported && isZoomed ? 'scale(2)' : '',
              ].filter(Boolean).join(' ') || undefined,
              transformOrigin: 'center center',
              transition: 'transform 0.2s ease',
            }}
          />
          {/* <div className="scanner-line" /> */}

          <button
            onClick={toggleZoom}
            className="absolute top-28 right-4 bg-purple-500 text-white px-4 py-2 rounded z-10"
          >
            {isZoomed ? '1x' : '2x'} Zoom
          </button>

          {torchSupported && (
            <button
              onClick={handleToggleTorch}
              className="absolute top-40 right-4 bg-yellow-500 text-white px-4 py-2 rounded z-10"
            >
              {isTorchOn ? '🔦 Torch Off' : '🔦 Torch On'}
            </button>
          )}

          <div className="scanner-line" />
        </div>
      )}

    </div>
  );
};

export default QrScan;

